import axios from 'axios';

import { getAPIUrl } from './baseAPI';

export interface QuestionCategory {
  id: number;
  name: string;
  description: string;
  badgeColor?: 'gray' | 'orange' | 'primary';
}

export interface QuestionCategorySummary {
  name: string;
  count: number;
  percentage: number;
}

export type QuestionCategoryType = 'THEME' | 'LIST';

export const FETCH_QUESTION_CATEGORIES = ({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) => ({
  path: waveId
    ? `/questionCategories/question/${questionId}/wave/${waveId}`
    : `/questionCategories/question/${questionId}`,
  version: 'v2' as const,
});

export async function fetchQuestionCategories({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) {
  return (
    await axios.get<QuestionCategory[]>(
      getAPIUrl(FETCH_QUESTION_CATEGORIES({ questionId, waveId })),
    )
  ).data;
}

export const FETCH_QUESTION_CATEGORY_SUMMARY = ({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) => ({
  path: waveId
    ? `/questionCategories/question/${questionId}/wave/${waveId}/summary`
    : `/questionCategories/question/${questionId}/summary`,
  version: 'v2' as const,
});

export async function fetchQuestionCategorySummary({
  questionId,
  waveId,
}: {
  questionId: number;
  waveId?: number;
}) {
  return (
    await axios.get<QuestionCategorySummary[]>(
      getAPIUrl(FETCH_QUESTION_CATEGORY_SUMMARY({ questionId, waveId })),
    )
  ).data;
}

export const GENERATE_QUESTION_CATEGORIES = () => ({
  path: '/questionCategories/generate',
  version: 'v2' as const,
});

export async function generateQuestionCategories({
  questionId,
  count,
  categoryType,
  waveId,
}: {
  questionId: number;
  count: number;
  categoryType: QuestionCategoryType;
  waveId?: number;
}) {
  return (
    await axios.post<QuestionCategory[]>(
      getAPIUrl(GENERATE_QUESTION_CATEGORIES()),
      { questionId, count, categoryType, waveId },
    )
  ).data;
}

export const CREATE_QUESTION_CATEGORY = () => ({
  path: '/questionCategories',
  version: 'v2' as const,
});

export async function createQuestionCategory({
  name,
  questionId,
  categoryType,
  description,
  waveId,
}: {
  name: string;
  questionId: number;
  categoryType: QuestionCategoryType;
  description?: string;
  waveId?: number;
}) {
  return (
    await axios.post<QuestionCategory>(getAPIUrl(CREATE_QUESTION_CATEGORY()), {
      name,
      questionId,
      categoryType,
      description,
      waveId,
    })
  ).data;
}

export const UPDATE_QUESTION_CATEGORY = ({ id }: { id: number }) => ({
  path: `/questionCategories/${id}`,
  version: 'v2' as const,
});

export async function updateQuestionCategory({
  id,
  name,
  description,
}: {
  id: number;
  name?: string;
  description?: string;
}) {
  return (
    await axios.put<QuestionCategory>(
      getAPIUrl(UPDATE_QUESTION_CATEGORY({ id })),
      { name, description },
    )
  ).data;
}

export const DELETE_QUESTION_CATEGORY = ({ id }: { id: number }) => ({
  path: `/questionCategories/${id}`,
  version: 'v2' as const,
});

export async function deleteQuestionCategory({ id }: { id: number }) {
  return (await axios.delete(getAPIUrl(DELETE_QUESTION_CATEGORY({ id })))).data;
}
